import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { AipMark } from "@/components/AipLogo";
import { BandPill, BandBar } from "@/components/Band";
import { getAccount } from "@/lib/store";
import { QBANK, scoreComposite, type CompositeResult } from "@/lib/scoring";
import { buildPortfolio, type PortfolioAccount } from "@/lib/extended";
import { ArrowLeft, Printer } from "lucide-react";

interface Stakeholder {
  name: string;
  role: string;
  posture: "champion" | "supporter" | "neutral" | "skeptic" | "blocker";
}

const STAKEHOLDER_FALLBACK: Stakeholder[] = [
  { name: "VP Engineering", role: "Champion", posture: "champion" },
  { name: "CFO", role: "Economic buyer", posture: "neutral" },
  { name: "CIO", role: "Approver", posture: "supporter" },
  { name: "Head of Security", role: "Reviewer", posture: "skeptic" },
];

const RISK_FALLBACK = [
  "Budget approval still routing through finance committee.",
  "Security review not yet scheduled.",
  "Competing priority for Q1 with internal tooling project.",
];

const NEXT_30_FALLBACK = [
  "Confirm decision date with economic buyer this week.",
  "Run security questionnaire with CISO team within 10 days.",
  "Draft mutual close plan; review jointly on Friday.",
  "Lock pricing approval with deal desk before EOM.",
];

export default function PlanPresentation() {
  const { accountId } = useParams<{ accountId: string }>();
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const onFocus = () => setVersion((v) => v + 1);
    window.addEventListener("focus", onFocus);
    window.addEventListener("hashchange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hashchange", onFocus);
    };
  }, []);

  const account = useMemo(() => getAccount(accountId), [accountId, version]);

  const portfolioEntry: PortfolioAccount | undefined = useMemo(
    () => buildPortfolio().find((p) => p.id === accountId),
    [accountId, version],
  );

  const result: CompositeResult | null = useMemo(
    () => (account ? scoreComposite(account.answers) : null),
    [account, version],
  );

  // For accounts not in the seeded store (e.g. Partner/Riverstone), fall back
  // to a synthetic plan view using portfolio metadata.
  const company = account?.company ?? portfolioEntry?.company;
  const industry = account?.industry ?? portfolioEntry?.industry;
  const rep = portfolioEntry?.rep ?? account?.rep;
  const stage = portfolioEntry?.stage ?? account?.stage;
  const arr = account?.arr ?? portfolioEntry?.arr;

  if (!company) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-8"
        style={{ background: "#FAFAF7" }}
      >
        <div
          style={{
            maxWidth: 480,
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 12,
            padding: "40px 36px",
            textAlign: "center",
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "#F4F4F5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <rect x="3" y="3" width="16" height="16" rx="2" stroke="#6B6B70" strokeWidth="1.5" />
              <path d="M7 8h8M7 11h8M7 14h5" stroke="#6B6B70" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#8E8E94",
              marginBottom: 10,
            }}
          >
            No plan found
          </div>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#0B0B0F", margin: "0 0 10px" }}>
            This account does not have a plan yet.
          </p>
          <p style={{ fontSize: 13, color: "#6B6B70", margin: "0 0 28px", lineHeight: 1.6 }}>
            A plan captures your Composite scores, stakeholder map, firm timeline, and the
            next 30-day actions for an account. Create one from the accounts list to start
            building your Friday presentation.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link
              href="/plans/new"
              style={{
                display: "inline-block",
                padding: "10px 22px",
                background: "#0B0B0F",
                color: "#FFFFFF",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Create a plan
            </Link>
            <Link
              href="/accounts"
              style={{
                display: "inline-block",
                padding: "10px 22px",
                background: "transparent",
                color: "#6B6B70",
                border: "1px solid #E5E5E7",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              View accounts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const ce = portfolioEntry?.compellingEvent ?? "No firm or formal timeline identified yet.";
  const ceDate = portfolioEntry?.ceDecisionDate;

  return (
    <div className="min-h-screen bg-background text-foreground print:bg-white print:text-black">
      {/* Top bar — hidden on print */}
      <header className="border-b border-border print:hidden">
        <div className="max-w-[1100px] mx-auto px-8 py-4 flex items-center justify-between">
          <Link href={`/account/${accountId}`} className="flex items-center gap-3">
            <AipMark className="w-6 h-6 text-foreground" />
            <span className="text-[13px] text-muted-foreground inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to scorecard
            </span>
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] transition-colors"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-8 py-12 print:px-0 print:py-6">
        {/* Title */}
        <div className="mb-12 print:mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-2">
            Friday plan presentation · {rep ?? "—"}
          </div>
          <h1 className="text-5xl print:text-4xl font-semibold tracking-tight leading-[1.05]">
            {company}
          </h1>
          <p className="text-lg text-muted-foreground mt-3">
            {industry} · {stage} · {arr ? `$${(arr / 1000).toFixed(0)}k ARR` : "ARR TBD"}
          </p>
        </div>

        {/* Account overview */}
        <Section number="01" title="Account overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Field label="Industry" value={industry ?? "—"} />
            <Field label="Stage" value={stage ?? "—"} />
            <Field label="Estimated ARR" value={arr ? `$${(arr / 1000).toFixed(0)}k` : "—"} />
            <Field label="Owner" value={rep ?? "—"} />
            <Field label="Close quarter" value={portfolioEntry?.closeQuarter ?? "—"} />
            <Field label="Days in stage" value={portfolioEntry?.daysInStage?.toString() ?? "—"} />
          </div>
        </Section>

        {/* Composite scores */}
        {result && (
          <Section number="02" title="Composite scores">
            <div className="flex items-baseline gap-4 mb-6">
              <span className="tabular text-6xl print:text-5xl font-semibold">
                {result.score.toFixed(1)}
              </span>
              <BandPill band={result.color} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {result.dimensions.map((dr) => {
                const def = QBANK.dimensions.find((d) => d.dimension_id === dr.dimension_id)!;
                return (
                  <div key={dr.dimension_id} className="card-shell-2 p-4 print:border print:border-neutral-300">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {def.short_name}
                    </div>
                    <div className="tabular text-2xl font-semibold mt-1">
                      {dr.color === "tbd" ? "—" : dr.score.toFixed(0)}
                    </div>
                    <div className="mt-2" title="Composite score band">
                      <BandBar band={dr.color} value={dr.color === "tbd" ? 0 : dr.score} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Stakeholder map */}
        <Section number={result ? "03" : "02"} title="Stakeholder map">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STAKEHOLDER_FALLBACK.map((s) => (
              <div key={s.name} className="card-shell-2 p-4 print:border print:border-neutral-300">
                <div className="text-[15px] font-medium tracking-tight">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.role}</div>
                <PostureBadge v={s.posture} />
              </div>
            ))}
          </div>
        </Section>

        {/* Firm / formal timeline */}
        <Section number={result ? "04" : "03"} title="Firm / formal timeline">
          <div className="card-shell-2 p-6 print:border print:border-neutral-300">
            <div className="text-2xl print:text-xl font-medium tracking-tight">{ce}</div>
            {ceDate && (
              <div className="text-sm text-muted-foreground mt-2">
                Decision date: <span className="tabular">{ceDate}</span>
              </div>
            )}
          </div>
        </Section>

        {/* Risks */}
        <Section number={result ? "05" : "04"} title="Risks">
          <ul className="space-y-3">
            {RISK_FALLBACK.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-[16px]">
                <span className="text-muted-foreground tabular text-[12px] mt-1.5 w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Next 30 days */}
        <Section number={result ? "06" : "05"} title="Next 30 days">
          <ol className="space-y-3">
            {NEXT_30_FALLBACK.map((n, i) => (
              <li key={i} className="flex items-start gap-3 text-[16px]">
                <span className="text-muted-foreground tabular text-[12px] mt-1.5 w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{n}</span>
              </li>
            ))}
          </ol>
        </Section>

        <div className="mt-16 pt-6 border-t border-border text-[11px] text-muted-foreground flex justify-between print:text-neutral-500">
          <span>Mi6 Attilius · Plan presentation · {company}</span>
          <span>Confidential</span>
        </div>
      </main>
    </div>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-14 print:mb-10">
      <div className="flex items-baseline gap-4 mb-5">
        <span className="tabular text-[12px] text-muted-foreground tracking-[0.18em]">
          {number}
        </span>
        <h2 className="text-2xl print:text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[18px] mt-1">{value}</div>
    </div>
  );
}

function PostureBadge({ v }: { v: "champion" | "supporter" | "neutral" | "skeptic" | "blocker" }) {
  const map: Record<typeof v, string> = {
    champion:  "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30",
    supporter: "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30",
    neutral:   "bg-neutral-700/40 text-neutral-300 ring-neutral-500/30",
    skeptic:   "bg-orange-500/10 text-orange-300 ring-orange-500/30",
    blocker:   "bg-rose-500/10 text-rose-300 ring-rose-500/30",
  };
  return (
    <span className={`mt-3 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ring-1 ring-inset ${map[v]}`}>
      {v}
    </span>
  );
}
